<script>
import AppLogo from "../../../components/AppLogo.vue";
import AppButton from "../../../components/AppButton.vue";

export default {
  name: "UtilityBillUpload" ,
  components: {AppButton, AppLogo},
  props: {
    loan: {},
  },

  data() {
    return {
      fileName: "",
      errors: null,
      loading: false,
      analyzing: false,
      aiErrors: null,
      pollTimer: null,
      pollCount: 0,
      maxPolls: 15,
      pollErrors: 0,
      overriding: false,
      canOverride: false,
    }
  },

  mounted() {
    // Check status of last document on page load (handles refresh during analysis or after failure)
    axios.get("/signup/utility-bill-status/" + this.loan.id)
      .then((response) => {
        if (response.data.status === "analyzing" || response.data.status === "pending") {
          this.analyzing = true;
          this.startPolling();
        } else if (response.data.status === "failed") {
          this.aiErrors = response.data.errors || ['analysis_error'];
          this.canOverride = response.data.can_override || false;
        }
      })
      .catch(() => {});
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    handleFileChange(event) {
      const files = event.target.files || [];
      if (files.length > 0) {
        this.fileName = files[0].name;
        this.errors = null;
        this.aiErrors = null;

        window.LogRocket?.track?.('document_file_selected', {
          document_type: 'utility_bill',
          file_size: files[0].size,
          file_type: files[0].type || files[0].name.split('.').pop()
        });
      } else {
        this.fileName = "";
      }
    },

    submitManual() {
      this.loading = true;
      this.errors = null;
      this.aiErrors = null;
      const formData = new FormData();

      const file = this.$refs.fileUpload.files[0];

      if (!file) {
        this.errors = { utility_bill: ["Please select a file to upload."] };
        this.loading = false;

        window.LogRocket?.track?.('document_upload_failed', {
          document_type: 'utility_bill',
          error: 'no_file_selected'
        });
        return;
      }

      window.LogRocket?.track?.('document_upload_attempted', {
        document_type: 'utility_bill',
        file_size: file.size,
        loan_id: this.loan.id
      });

      formData.append("utility_bill", file);

      axios.post("/signup/utility-bill-upload/" + this.loan.id, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })
        .then((response) => {
            window.LogRocket?.track?.('document_upload_success', {
              document_type: 'utility_bill',
              file_size: file.size,
              loan_id: this.loan.id
            });

            this.loading = false;
            this.analyzing = true;
            this.startPolling();
        })
        .catch((error) => {
          this.errors = { utility_bill: ["An error occurred. Please try again."] };

          if (error.response?.status === 413) {
            this.errors = { utility_bill: ["The file is too large. Please upload a file less than 20MB."] };

            window.LogRocket?.track?.('document_upload_failed', {
              document_type: 'utility_bill',
              error: 'file_too_large',
              file_size: file.size
            });
          }
          if (error.response?.status === 422) {
            this.errors = error.response.data.errors;

            window.LogRocket?.track?.('document_upload_failed', {
              document_type: 'utility_bill',
              error: 'validation_failed',
              error_details: JSON.stringify(error.response.data.errors)
            });
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    startPolling() {
      this.pollCount = 0;
      this.pollTimer = setInterval(() => {
        this.pollCount++;
        if (this.pollCount > this.maxPolls) {
          this.stopPolling();
          this.analyzing = false;
          this.aiErrors = ['analysis_timeout'];
          this.resetFileInput();
          return;
        }
        this.checkStatus();
      }, 4000);
    },

    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer);
        this.pollTimer = null;
      }
    },

    checkStatus() {
      axios.get("/signup/utility-bill-status/" + this.loan.id)
        .then((response) => {
          this.pollErrors = 0;
          const { status, errors } = response.data;

          if (status === "passed") {
            this.stopPolling();
            window.location.reload();
          }

          if (status === "failed") {
            this.stopPolling();
            this.analyzing = false;
            this.aiErrors = errors || ['analysis_error'];
            this.canOverride = response.data.can_override || false;
            this.resetFileInput();

            window.LogRocket?.track?.('document_ai_analysis_failed', {
              document_type: 'utility_bill',
              errors: errors,
              loan_id: this.loan.id
            });
          }
        })
        .catch(() => {
          this.pollErrors++;
          // Only stop after 3 consecutive failures (tolerate transient network issues)
          if (this.pollErrors >= 3) {
            this.stopPolling();
            this.analyzing = false;
            this.aiErrors = ['analysis_error'];
            this.resetFileInput();
          }
        });
    },

    resetFileInput() {
      this.fileName = "";
      if (this.$refs.fileUpload) {
        this.$refs.fileUpload.value = "";
      }
    },

    submitOverride() {
      this.overriding = true;
      axios.post("/signup/utility-bill-override/" + this.loan.id)
        .then(() => {
          window.location.reload();
        })
        .catch(() => {
          this.overriding = false;
          this.errors = { utility_bill: ["Something went wrong. Please try again."] };
        });
    },

    getErrorMessage(code) {
      const messages = {
        'not_utility_bill': 'The uploaded document does not appear to be a utility bill. Please upload a valid utility bill.',
        'missing_name': 'We could not find a name on the bill. Please upload a utility bill that clearly shows the account holder name.',
        'name_mismatch': 'The name on the bill does not match your name on file. Please upload a bill in your name.',
        'missing_address': 'We could not find an address on the bill. Please upload a utility bill that clearly shows the service address.',
        'address_mismatch': 'The address on the bill does not match your address on file. Please upload a bill with your current address.',
        'missing_date': 'We could not find a date on the bill. Please upload a utility bill that clearly shows the bill date.',
        'bill_too_old': 'The bill appears to be more than 60 days old. Please upload a more recent utility bill.',
        'analysis_error': 'We had trouble analyzing your document. Please try uploading again.',
        'analysis_timeout': 'Analysis is taking longer than expected. Please try uploading again.',
      };
      return messages[code] || 'There was an issue with your document. Please try uploading again.';
    },
  },

  computed: {
    getError() {
      if (this.errors) {
        return this.errors.utility_bill ? this.errors.utility_bill[0] : null;
      }
    },
    showOverrideButton() {
      return this.canOverride;
    },
  }
}
</script>

<template>
  <app-wrapper>
    <app-header :loan="loan" medium-max-width="max-w-1000">
      <template #navigation>
        <div class="max-w-200">
          <h5 class="font-zen-dots text-white">
            <AppLogo/>
          </h5>
        </div>
      </template>
      <template #top>
        <div class="w-full flex items-center">
          <div class="flex-auto">
            Your desired loan amount
            <h2 class="mb-0 mt-2 md:mt-4 md:text-6xl">{{ loan.amount | money(0) }}</h2>
          </div>
          <div class="flex-initial">
            <i class="fa fa-check-circle text-success text-7xl"/>
          </div>
        </div>
      </template>
    </app-header>
    <app-body medium-max-width="max-w-500" footer>

    <template #title>
      Utility Bill Upload
    </template>

    <template #main>
      <!-- Analyzing state -->
      <div v-if="analyzing" class="mt-4 text-center">
        <div class="flex justify-center mb-4">
          <svg class="animate-spin h-10 w-10 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p class="text-lg font-semibold">Analyzing your document...</p>
        <p class="text-gray-500 mt-2">This may take up to a minute. Please don't close this page.</p>
      </div>

      <!-- Upload state -->
      <div v-else>
        <div class="mt-4">
          <p class="text-lg">
            We're unable to verify your address as current. Please upload your most recent utility bill:
          </p>
        </div>

        <!-- AI errors -->
        <div v-if="aiErrors && aiErrors.length" class="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
          <p class="font-semibold text-red-700 mb-2">We found issues with your document:</p>
          <ul class="list-disc pl-5 text-red-600">
            <li v-for="err in aiErrors" :key="err">{{ getErrorMessage(err) }}</li>
          </ul>
        </div>

        <!-- User override button -->
        <div v-if="showOverrideButton" class="mt-4 text-center">
          <p class="text-gray-600 text-sm mb-2">If you believe this bill is correct, you can confirm and continue.</p>
          <AppButton
              :loading="overriding"
              :style="'outline'"
              @click="submitOverride">I confirm this bill is correct</AppButton>
        </div>

        <div class="mt-4">
          <div class="md:flex justify-between mb-3">
            <div
                v-if="getError"
                class="flex-initial mt-2 sm:mt-0 md:ml-2 font-semibold text-red-500"
            >
              {{ getError }}
            </div>
          </div>
          <div class="md:flex">
            <input
                data-testid="utility_bill"
                ref="fileUpload"
                type="file"
                name="utility_bill"
                id="utility_bill_file_input"
                accept=".pdf,.jpg,.jpeg,.png,.bmp"
                @change="handleFileChange"
                tabindex="-1"
                class="hidden"
            />
            <label
                for="utility_bill_file_input"
                class="bg-white p-14 w-full flex justify-center items-center font-semibold text-lg border-2 border-dashed border-gray-200 rounded-lg cursor-pointer touch-manipulation block"
            >
              <template v-if="!fileName">
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-8 h-8 mr-2"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                  />
                </svg>
                Upload Document
              </template>
              <template v-else>
                <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="w-8 h-8 mr-2"
                >
                  <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M10.125 2.25h-4.5c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125v-9M10.125 2.25h.375a9 9 0 019 9v.375M10.125 2.25A3.375 3.375 0 0113.5 5.625v1.5c0 .621.504 1.125 1.125 1.125h1.5a3.375 3.375 0 013.375 3.375M9 15l2.25 2.25L15 12"
                  />
                </svg>
                {{ fileName }}
              </template>
            </label>
          </div>
        </div>

        <div class="flex justify-center items-center mt-8">
          <AppButton
              data-testid="submit"
              :loading="loading"
              :style="'solid'"
              @click="submitManual">Submit</AppButton>
        </div>
      </div>
    </template>

    </app-body>
  </app-wrapper>
</template>

<style scoped>
/* Mobile touch optimization */
.touch-manipulation {
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  -khtml-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  user-select: none;
}

/* Mobile hover state fix */
@media (hover: none) and (pointer: coarse) {
  .cursor-pointer:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
}

/* iOS Safari file input fixes */
input[type="file"] {
  -webkit-appearance: none;
  appearance: none;
}
</style>
